import type { Locale } from './types'

/**
 * PROGRAMAREA UNUI RAPORT: socotelile, fara nicio legatura cu ecranul.
 *
 * Ora programarii e ora de la Bucuresti (`Europe/Bucharest`), oricare ar fi
 * ceasul browserului: un raport "luni la 08:00" pleaca luni la 08:00 la
 * Bucuresti, si iarna, si vara. Trecerea in UTC se face prin `Intl`, fara
 * biblioteca.
 *
 * Lunar pe ziua 31 inseamna "ultima zi a lunii" in lunile mai scurte - altfel
 * februarie n-ar primi raportul deloc.
 *
 * Nimic de aici nu trimite vreun email: in etapa asta trimiterea nu exista,
 * iar ecranul arata doar CAND ar pleca.
 */

export type Frequency = 'daily' | 'weekly' | 'monthly'

export const TIMEZONE = 'Europe/Bucharest'

export interface ScheduleRule {
  frequency: Frequency
  /** 0 = duminica ... 6 = sambata, ca `Date.getDay()`. */
  weekday: number
  /** 1..31; peste ultima zi a lunii cade pe ultima zi. */
  month_day: number
  /** "HH:MM", ora de la Bucuresti. */
  time: string
  timezone: string
}

export const WEEKDAYS: Record<Locale, string[]> = {
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  ro: ['duminica', 'luni', 'marti', 'miercuri', 'joi', 'vineri', 'sambata'],
}

export const WEEKDAYS_SHORT: Record<Locale, string[]> = {
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  ro: ['dum', 'lun', 'mar', 'mie', 'joi', 'vin', 'sam'],
}

const MONTHS: Record<Locale, string[]> = {
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  ro: ['ianuarie', 'februarie', 'martie', 'aprilie', 'mai', 'iunie', 'iulie', 'august', 'septembrie', 'octombrie', 'noiembrie', 'decembrie'],
}

/** Ordinea din alegerea zilei: luni intai, duminica la sfarsit. */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0]

export function parseTime(time: string): { h: number; m: number } {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim())
  if (!match) return { h: 8, m: 0 }
  return { h: Math.min(23, Number(match[1])), m: Math.min(59, Number(match[2])) }
}

interface Parts {
  y: number
  mo: number
  d: number
  h: number
  mi: number
  wd: number
}

const formatters = new Map<string, Intl.DateTimeFormat>()

function formatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' })
    formatters.set(tz, f)
  }
  return f
}

/** Ce arata ceasul din `tz` in clipa `at`. */
export function zonedParts(at: Date, tz = TIMEZONE): Parts {
  const out: Record<string, number> = {}
  for (const p of formatter(tz).formatToParts(at)) if (p.type !== 'literal') out[p.type] = Number(p.value)
  const y = out.year!
  const mo = out.month!
  const d = out.day!
  return { y, mo, d, h: (out.hour ?? 0) % 24, mi: out.minute ?? 0, wd: new Date(Date.UTC(y, mo - 1, d)).getUTCDay() }
}

/** Clipa (UTC) in care ceasul din `tz` arata data si ora date. */
export function zonedToUtc(y: number, mo: number, d: number, h: number, mi: number, tz = TIMEZONE): Date {
  const wall = Date.UTC(y, mo - 1, d, h, mi)
  let guess = wall
  for (let i = 0; i < 3; i++) {
    const p = zonedParts(new Date(guess), tz)
    const seen = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi)
    const diff = wall - seen
    if (diff === 0) break
    guess += diff
  }
  return new Date(guess)
}

export function daysInMonth(y: number, mo: number): number {
  return new Date(Date.UTC(y, mo, 0)).getUTCDate()
}

/** Urmatoarele `n` plecari dupa clipa `from` (strict dupa). */
export function nextRuns(rule: ScheduleRule, from: Date, n = 3): Date[] {
  const tz = rule.timezone || TIMEZONE
  const { h, m } = parseTime(rule.time)
  const start = zonedParts(from, tz)
  const out: Date[] = []
  /* Pe zile din calendarul de la Bucuresti; 400 ajunge si pentru lunar x 3. */
  for (let i = 0; i < 1200 && out.length < n; i++) {
    const day = new Date(Date.UTC(start.y, start.mo - 1, start.d + i))
    const y = day.getUTCFullYear()
    const mo = day.getUTCMonth() + 1
    const d = day.getUTCDate()
    let hit = false
    if (rule.frequency === 'daily') hit = true
    else if (rule.frequency === 'weekly') hit = day.getUTCDay() === rule.weekday
    else hit = d === Math.min(Math.max(1, rule.month_day), daysInMonth(y, mo))
    if (!hit) continue
    const at = zonedToUtc(y, mo, d, h, m, tz)
    if (at.getTime() > from.getTime()) out.push(at)
  }
  return out
}

const pad = (n: number) => String(n).padStart(2, '0')

/** "Mon 12 Oct 2026, 08:00" / "lun 12 octombrie 2026, 08:00", la Bucuresti. */
export function formatRun(at: Date, locale: Locale, tz = TIMEZONE): string {
  const p = zonedParts(at, tz)
  const month = MONTHS[locale][p.mo - 1]!
  const short = locale === 'en' ? month.slice(0, 3) : month
  return `${WEEKDAYS_SHORT[locale][p.wd]} ${p.d} ${short} ${p.y}, ${pad(p.h)}:${pad(p.mi)}`
}

const ordinal = (n: number) => {
  const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th')
  return `${n}${s}`
}

/** Propozitia omeneasca: "Every Monday at 08:00, Bucharest time". */
export function summary(rule: ScheduleRule, locale: Locale): string {
  const { h, m } = parseTime(rule.time)
  const time = `${pad(h)}:${pad(m)}`
  if (locale === 'ro') {
    const tail = `la ${time}, ora Bucurestiului`
    if (rule.frequency === 'daily') return `In fiecare zi ${tail}`
    if (rule.frequency === 'weekly') return `In fiecare ${WEEKDAYS.ro[rule.weekday]} ${tail}`
    return rule.month_day >= 31 ? `In ultima zi a fiecarei luni ${tail}` : `Pe ${rule.month_day} ale fiecarei luni ${tail}`
  }
  const tail = `at ${time}, Bucharest time`
  if (rule.frequency === 'daily') return `Every day ${tail}`
  if (rule.frequency === 'weekly') return `Every ${WEEKDAYS.en[rule.weekday]} ${tail}`
  return rule.month_day >= 31 ? `On the last day of every month ${tail}` : `On the ${ordinal(rule.month_day)} of every month ${tail}`
}

/** Eticheta scurta din lista: "Weekly · Mon 08:00". */
export function shortLabel(rule: ScheduleRule, locale: Locale): string {
  const { h, m } = parseTime(rule.time)
  const time = `${pad(h)}:${pad(m)}`
  const ro = locale === 'ro'
  if (rule.frequency === 'daily') return `${ro ? 'Zilnic' : 'Daily'} · ${time}`
  if (rule.frequency === 'weekly') return `${ro ? 'Saptamanal' : 'Weekly'} · ${WEEKDAYS_SHORT[locale][rule.weekday]} ${time}`
  const day = rule.month_day >= 31 ? (ro ? 'ultima zi' : 'last day') : ro ? `ziua ${rule.month_day}` : `day ${rule.month_day}`
  return `${ro ? 'Lunar' : 'Monthly'} · ${day} ${time}`
}

/** Insigna din "Rapoartele mele": "Weekly · Mon 08:00 · 3 recipients". */
export function scheduleBadge(s: ScheduleRule & { recipients: string[] }, locale: Locale): string {
  const n = s.recipients.length
  const who = locale === 'ro' ? `${n} ${n === 1 ? 'destinatar' : 'destinatari'}` : `${n} ${n === 1 ? 'recipient' : 'recipients'}`
  return `${shortLabel(s, locale)} · ${who}`
}

/** Perioada pe care o poarta un email: ce scrie in locul lui `{period}`. */
export function periodLabel(frequency: Frequency, at: Date, locale: Locale, tz = TIMEZONE): string {
  const p = zonedParts(at, tz)
  if (frequency === 'daily') return `${pad(p.d)}.${pad(p.mo)}.${p.y}`
  if (frequency === 'weekly') {
    /* Saptamana care s-a incheiat inainte de plecare: de luni pana duminica. */
    const end = new Date(Date.UTC(p.y, p.mo - 1, p.d - 1))
    const back = (end.getUTCDay() + 6) % 7
    const monday = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate() - back))
    const fmt = (x: Date) => `${pad(x.getUTCDate())}.${pad(x.getUTCMonth() + 1)}`
    const sunday = new Date(monday.getTime() + 6 * 86400000)
    return `${fmt(monday)}–${fmt(sunday)}.${sunday.getUTCFullYear()}`
  }
  /* Lunar: luna incheiata. */
  const prev = new Date(Date.UTC(p.y, p.mo - 2, 1))
  const name = MONTHS[locale][prev.getUTCMonth()]!
  return `${locale === 'en' ? name : name.charAt(0).toUpperCase() + name.slice(1)} ${prev.getUTCFullYear()}`
}

export function defaultSubject(name: string): string {
  return `${name} – {period}`
}

export function fillSubject(subject: string, period: string): string {
  return subject.replaceAll('{period}', period)
}

/* ---------------- Adresele ---------------- */

const EMAIL = /^[^\s@,;<>()"]+@[^\s@,;<>()"]+\.[^\s@,;<>()".]{2,}$/

export function isEmail(text: string): boolean {
  return EMAIL.test(text.trim())
}

/**
 * Bucata lipita sau scrisa: imparte pe virgula, punct si virgula, spatii sau
 * randuri noi; scoate forma "Nume <adresa>". Intoarce adresele in ordine, cu
 * literele mici, fara dubluri fata de `existing`.
 */
export function splitEmails(text: string, existing: string[] = []): string[] {
  const seen = new Set(existing.map((e) => e.toLowerCase()))
  const out: string[] = []
  const pieces = text.split(/[,;\n\r]+/).flatMap((chunk) => {
    if (!chunk.includes('<')) return chunk.split(/\s+/)
    /* "Nume Prenume <adresa>": numele cade, raman adresele (si ce mai are @ pe langa). */
    const inside = [...chunk.matchAll(/<([^>]*)>/g)].map((m) => m[1]!)
    const outside = chunk.replace(/<[^>]*>/g, ' ').split(/\s+/).filter((w) => w.includes('@'))
    return [...inside, ...outside]
  })
  for (const raw of pieces) {
    const part = raw.replace(/^["']|["']$/g, '').trim().toLowerCase()
    if (part === '' || seen.has(part)) continue
    seen.add(part)
    out.push(part)
  }
  return out
}
